import { useRef, useEffect, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry';
import InteractionHints from './InteractionHints';
import { applyFabricTexture, updateTextureZoom, remapUVsToWorldSpace, setMaxAnisotropy } from './fabricTexture';
import FabricLoadingOverlay from './FabricLoadingOverlay';

const _isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
const DEFAULT_CAM = _isMobile ? { x: 0, y: 0.76, z: -0.91 } : { x: 0, y: 0.95, z: -1.15 };

const PremiumCushionPreview = ({ length, width, thickness, fabricColor, fabricImageUrl, fabricZoom = 1, zipPlacement = 'length', showZip = true, rawLength, rawWidth, rawThickness, unit = 'cm', activeField = null, pipingOption = 'none', pipingColor = null, pipingImageUrl = null, cameraDefault = null }) => {
  const containerRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const cushionRef = useRef(null);
  const cornerMeshesRef = useRef([]);
  const [fabricLoading, setFabricLoading] = useState(false);
  const [pipingLoading, setPipingLoading] = useState(false);
  const zipGroupRef = useRef(null);
  const pipingTopRef = useRef(null);
  const pipingBottomRef = useRef(null);
  const sceneRef = useRef(null);
  const animFrameRef = useRef(null);
  const targetScaleRef = useRef(new THREE.Vector3(1, 1, 1));
  const initializedRef = useRef(false);
  const resetCameraRef = useRef(false);
  const containerWidthRef = useRef(600);
  const dimLabelsRef = useRef({ length: null, width: null, thickness: null });
  const activeFieldRef = useRef(activeField);

  const scaleFactor = 0.008;
  const l = Math.max(length * scaleFactor, 0.15);
  const w = Math.max(width * scaleFactor, 0.1);
  const t = Math.max(thickness * scaleFactor, 0.03);
  useEffect(() => { activeFieldRef.current = activeField; }, [activeField]);

  const color = fabricColor || '#5a8a5a';

  // Initialize scene
  useEffect(() => {
    if (!containerRef.current || initializedRef.current) return;
    initializedRef.current = true;

    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    containerWidthRef.current = rect.width;

    const scene = new THREE.Scene();

    const camera = new THREE.PerspectiveCamera(35, rect.width / rect.height, 0.01, 100);
    // If a per-product cameraDefault is provided AND we're on desktop, use it
    // verbatim. Otherwise fall back to the global DEFAULT_CAM and let the reset
    // logic auto-fit based on dimensions.
    const useCustomCam = cameraDefault && rect.width >= 500;
    if (useCustomCam) {
      camera.position.set(cameraDefault.position.x, cameraDefault.position.y, cameraDefault.position.z);
      const t = cameraDefault.target || { x: 0, y: 0, z: 0 };
      camera.lookAt(t.x, t.y, t.z);
    } else {
      camera.position.set(DEFAULT_CAM.x, DEFAULT_CAM.y, DEFAULT_CAM.z);
      camera.lookAt(0, 0, 0);
    }
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
    renderer.setSize(rect.width, rect.height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.2;
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;
    setMaxAnisotropy(renderer);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enablePan = true;
    controls.screenSpacePanning = true;
    controls.panSpeed = 1.2;
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.minDistance = 0.15;
    controls.maxDistance = 4;
    controls.minPolarAngle = 0;
    controls.maxPolarAngle = Math.PI;
    controls.touches = { ONE: THREE.TOUCH.ROTATE, TWO: THREE.TOUCH.DOLLY_PAN };
    controls.mouseButtons = { LEFT: THREE.MOUSE.ROTATE, MIDDLE: THREE.MOUSE.PAN, RIGHT: THREE.MOUSE.PAN };
    controls.target.set(0, 0, 0);
    controls.addEventListener('start', () => { resetCameraRef.current = false; });
    renderer.domElement.__orbitControls = controls;
    renderer.domElement.__renderFrame = () => renderer.render(scene, camera);
    controlsRef.current = controls;

    // Lighting
    scene.add(new THREE.AmbientLight(0xffffff, 0.6));
    const dirLight1 = new THREE.DirectionalLight(0xffffff, 1.2);
    dirLight1.position.set(3, 5, 3);
    dirLight1.castShadow = true;
    dirLight1.shadow.mapSize.set(1024, 1024);
    scene.add(dirLight1);
    scene.add(new THREE.DirectionalLight(0xffffff, 0.4).translateX(-2).translateY(3).translateZ(-1));
    scene.add(new THREE.PointLight(0xffffff, 0.3).translateY(2));

    // Shadow ground
    const ground = new THREE.Mesh(
      new THREE.PlaneGeometry(5, 5),
      new THREE.ShadowMaterial({ opacity: 0.15 })
    );
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -0.05;
    ground.receiveShadow = true;
    scene.add(ground);

    // Cushion — single solid color body
    const geo = new RoundedBoxGeometry(1, 1, 1, 6, 0.04);
    remapUVsToWorldSpace(geo, l, t, w);
    const cushion = new THREE.Mesh(
      geo,
      new THREE.MeshStandardMaterial({ color: new THREE.Color(color), roughness: 0.85, metalness: 0.02 })
    );
    cushion.castShadow = true;
    cushion.scale.set(l, t, w);
    scene.add(cushion);
    cushionRef.current = cushion;

    // 4 corner vertical seam lines
    const cornerSeamColor = new THREE.Color(color).multiplyScalar(0.55);
    const cornerMeshes = [];
    for (let i = 0; i < 4; i++) {
      const cm = new THREE.Mesh(
        new THREE.CylinderGeometry(0.002, 0.002, 1, 6),
        new THREE.MeshStandardMaterial({ color: cornerSeamColor, roughness: 1.0, metalness: 0 })
      );
      scene.add(cm);
      cornerMeshes.push(cm);
    }
    cornerMeshesRef.current = cornerMeshes;

    // Zipper group
    const zipGroup = new THREE.Group();
    scene.add(zipGroup);
    zipGroupRef.current = zipGroup;

    // Zipper track
    const trackMat = new THREE.MeshBasicMaterial({ color: 0xf5f5f5 });
    zipGroup.add(new THREE.Mesh(new THREE.BoxGeometry(1, 0.006, 0.008), trackMat));

    // Zipper teeth
    const toothMat = new THREE.MeshBasicMaterial({ color: 0xf5f5f5 });
    for (let i = 0; i < 48; i++) {
      const tooth = new THREE.Mesh(new THREE.BoxGeometry(0.01, 0.006, 0.006), toothMat);
      const zOff = (i % 2 === 0 ? 1 : -1) * 0.010;
      tooth.position.set(-0.48 + i * 0.02, 0, zOff);
      zipGroup.add(tooth);
    }

    // Zipper pull — positioned at right end
    const pull = new THREE.Mesh(
      new THREE.BoxGeometry(0.03, 0.005, 0.006),
      new THREE.MeshBasicMaterial({ color: 0xf5f5f5 })
    );
    pull.position.set(0.48, 0.004, 0.008);
    zipGroup.add(pull);

    // Piping rings (top + bottom edges)
    const pipingMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.6, metalness: 0.05 });
    const pipingTop = new THREE.Mesh(new THREE.BufferGeometry(), pipingMat);
    const pipingBottom = new THREE.Mesh(new THREE.BufferGeometry(), pipingMat.clone());
    pipingTop.visible = false;
    pipingBottom.visible = false;
    scene.add(pipingTop);
    scene.add(pipingBottom);
    pipingTopRef.current = pipingTop;
    pipingBottomRef.current = pipingBottom;
    sceneRef.current = scene;

    // Animation loop
    const targetCamPos = new THREE.Vector3();
    const animate = () => {
      animFrameRef.current = requestAnimationFrame(animate);
      controls.update();

      if (cushion) {
        cushion.scale.lerp(targetScaleRef.current, 0.08);

        const sl = cushion.scale.x;
        const sw = cushion.scale.z;
        const st = cushion.scale.y;

        // Corner seams
        const cx = sl * 0.47;
        const cz = sw * 0.47;
        const cornerPositions = [
          [-cx, 0, -cz],
          [cx, 0, -cz],
          [cx, 0, cz],
          [-cx, 0, cz],
        ];
        for (let i = 0; i < 4; i++) {
          cornerMeshes[i].position.set(...cornerPositions[i]);
          cornerMeshes[i].scale.set(1, st * 0.95, 1);
        }

        // Update piping ring Y positions — outer corner edge
        if (pipingTop.visible) {
          const pInset = 0.003 * 0.25;
          pipingTop.position.y = st / 2 - pInset;
          pipingBottom.position.y = -(st / 2 - pInset);
        }

        ground.position.y = -st / 2 - 0.002;

        // Camera reset — responsive
        if (resetCameraRef.current) {
          const maxDim = Math.max(sl, sw);
          const isMobile = containerWidthRef.current < 500;
          // Per-product custom camera preset (desktop only): snap to the saved
          // position/target verbatim instead of auto-fitting.
          const targetVec = new THREE.Vector3(0, 0, 0);
          if (cameraDefault && !isMobile) {
            targetCamPos.set(cameraDefault.position.x, cameraDefault.position.y, cameraDefault.position.z);
            const t = cameraDefault.target || { x: 0, y: 0, z: 0 };
            targetVec.set(t.x, t.y, t.z);
          } else if (isMobile) {
            // Mobile: FOV-based fit so the camera auto-zooms out when the cushion
            // is long/wide (e.g. 200cm × 55cm). Uses the same approach as Hinged.
            const containerH = rendererRef.current ? rendererRef.current.domElement.clientHeight : 400;
            const containerW = containerWidthRef.current || 400;
            const aspect = containerW / Math.max(containerH, 1);
            const fovRad = (35 / 2) * Math.PI / 180;
            const verticalFit = (sw * 0.5) / Math.tan(fovRad);
            const horizontalFit = (sl * 0.5) / (Math.tan(fovRad) * aspect);
            const margin = 1.45;
            const dist = Math.max(verticalFit, horizontalFit) * margin + sw * 0.35;
            const dirLen = Math.sqrt(DEFAULT_CAM.x ** 2 + DEFAULT_CAM.y ** 2 + DEFAULT_CAM.z ** 2) || 1;
            targetCamPos.set(
              (DEFAULT_CAM.x / dirLen) * dist,
              (DEFAULT_CAM.y / dirLen) * dist,
              (DEFAULT_CAM.z / dirLen) * dist
            );
          } else {
            const distMult = 1.35;
            const distBase = 0.3;
            const camScale = 1.5;
            const dist = maxDim * distMult + distBase;
            targetCamPos.set(
              DEFAULT_CAM.x / camScale * dist,
              DEFAULT_CAM.y / camScale * dist,
              DEFAULT_CAM.z / camScale * dist
            );
          }
          camera.position.lerp(targetCamPos, 0.15);
          controls.target.lerp(targetVec, 0.15);
          if (camera.position.distanceTo(targetCamPos) < 0.02) {
            resetCameraRef.current = false;
            controls.saveState();
          }
        }
      }

      renderer.render(scene, camera);

      // Project 3D dimension anchor points to 2D for HTML labels + highlight active field
      const af = activeFieldRef.current;
      const activeBg = 'rgba(255,215,0,0.92)';
      const defaultBg = 'rgba(255,255,255,0.85)';
      const activeText = '#1a1a1a';
      const defaultText = '#374151';

      if (cushion && dimLabelsRef.current.length && dimLabelsRef.current.width && dimLabelsRef.current.thickness) {
        const sl = cushion.scale.x;
        const sw = cushion.scale.z;
        const st = cushion.scale.y;
        const containerRect = container.getBoundingClientRect();

        const project = (worldPos) => {
          const v = worldPos.clone().project(camera);
          return {
            x: (v.x * 0.5 + 0.5) * containerRect.width,
            y: (-v.y * 0.5 + 0.5) * containerRect.height,
            visible: v.z < 1
          };
        };

        const setLabelHighlight = (el, isActive, pos) => {
          el.style.left = pos.x + 'px';
          el.style.top = pos.y + 'px';
          el.style.opacity = pos.visible ? '1' : '0';
          el.style.backgroundColor = isActive ? activeBg : defaultBg;
          el.style.color = isActive ? activeText : defaultText;
        };

        // Length label
        const lengthPos = project(new THREE.Vector3(0, -st / 2 - 0.01, -sw / 2 - 0.02));
        setLabelHighlight(dimLabelsRef.current.length, af === 'length', lengthPos);

        // Width label
        const widthPos = project(new THREE.Vector3(sl / 2 + 0.02, -st / 2 - 0.01, 0));
        setLabelHighlight(dimLabelsRef.current.width, af === 'width', widthPos);

        // Thickness label
        const thicknessPos = project(new THREE.Vector3(-sl / 2 - 0.02, 0, -sw / 2 - 0.02));
        const tl = dimLabelsRef.current.thickness;
        tl.style.left = thicknessPos.x + 'px';
        tl.style.top = thicknessPos.y + 'px';
        tl.style.opacity = thicknessPos.visible ? '1' : '0';
        tl.style.backgroundColor = af === 't' ? activeBg : defaultBg;
        tl.style.color = af === 't' ? activeText : defaultText;
      }
    };
    animate();

    const handleResize = () => {
      if (!container) return;
      const r = container.getBoundingClientRect();
      containerWidthRef.current = r.width;
      camera.aspect = r.width / r.height;
      camera.updateProjectionMatrix();
      renderer.setSize(r.width, r.height);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      controls.dispose();
      renderer.dispose();
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
      initializedRef.current = false;
    };
  }, []);

  // Update target dimensions + trigger camera reset + remap UVs
  useEffect(() => {
    targetScaleRef.current.set(l, t, w);
    resetCameraRef.current = true;
    if (cushionRef.current) {
      remapUVsToWorldSpace(cushionRef.current.geometry, l, t, w);
    }
  }, [l, t, w]);

  // Update color / texture
  useEffect(() => {
    if (cushionRef.current) {
      const needsLoad = fabricImageUrl && (fabricImageUrl.includes('warwick.com') || fabricImageUrl.startsWith('/api/fabric-images'));
      if (needsLoad) setFabricLoading(true);
      applyFabricTexture(cushionRef.current, fabricImageUrl, color, fabricZoom, () => setFabricLoading(false));
    }
    cornerMeshesRef.current.forEach(cm => {
      cm.material.color.copy(new THREE.Color(color).multiplyScalar(0.55));
    });
  }, [color, fabricImageUrl]);

  // Update texture zoom
  useEffect(() => {
    if (cushionRef.current) updateTextureZoom(cushionRef.current, fabricZoom);
  }, [fabricZoom]);

  // Update zip placement — 98% of edge length
  useEffect(() => {
    if (!zipGroupRef.current || !cushionRef.current) return;
    zipGroupRef.current.visible = showZip;
    if (!showZip) return;
    const updateZip = () => {
      const sl = cushionRef.current.scale.x;
      const sw = cushionRef.current.scale.z;
      if (zipPlacement === 'length') {
        zipGroupRef.current.position.set(0, 0, sw / 2 + 0.001);
        zipGroupRef.current.rotation.set(0, 0, 0);
        zipGroupRef.current.scale.set(sl * 0.98, 1, 1);
      } else {
        zipGroupRef.current.position.set(-sl / 2 - 0.001, 0, 0);
        zipGroupRef.current.rotation.set(0, Math.PI / 2, 0);
        zipGroupRef.current.scale.set(sw * 0.98, 1, 1);
      }
    };
    updateZip();
    const interval = setInterval(updateZip, 50);
    return () => clearInterval(interval);
  }, [zipPlacement]);

  // Update piping rings
  useEffect(() => {
    const pt = pipingTopRef.current;
    const pb = pipingBottomRef.current;
    if (!pt || !pb) return;

    const showPiping = pipingOption !== 'none';
    pt.visible = showPiping;
    pb.visible = showPiping;
    if (!showPiping) { setPipingLoading(false); return; }

    // Determine piping color and image
    const pColor = pipingOption === 'same' ? (fabricColor || '#5a8a5a') : (pipingColor || '#f5f0eb');
    const pImageUrl = pipingOption === 'same' ? fabricImageUrl : pipingImageUrl;
    const needsLoad = pImageUrl && (pImageUrl.includes('warwick.com') || pImageUrl.startsWith('/api/fabric-images'));
    if (needsLoad) setPipingLoading(true);
    let __remaining = 2;
    const __done = () => { __remaining -= 1; if (__remaining <= 0) setPipingLoading(false); };

    // Apply texture or solid color to both piping meshes
    applyFabricTexture(pt, pImageUrl, pColor, fabricZoom, needsLoad ? __done : null);
    applyFabricTexture(pb, pImageUrl, pColor, fabricZoom, needsLoad ? __done : null);

    // RoundedBoxGeometry uses radius=0.04 in geometry space (1x1x1).
    // After scaling by (l, t, w), the corner radii become elliptical.
    const R = 0.04;
    const hx = l / 2;
    const hz = w / 2;
    const rx = R * l;
    const rz = R * w;
    // Position piping at bevel's 45° midpoint for a snug edge fit
    const sin45 = Math.SQRT1_2;
    const tubeRadius = 0.003;
    // Minimal XZ inset — piping hugs the very outer edge
    const inset = tubeRadius * 0.25;
    const ihx = hx - inset;
    const ihz = hz - inset;

    const arcSegs = 48;
    const pts = [];

    // Helper: add elliptical arc points
    const addArc = (cx, cz, startAngle) => {
      for (let i = 0; i <= arcSegs; i++) {
        const a = startAngle + (Math.PI / 2) * (i / arcSegs);
        pts.push(new THREE.Vector3(cx + rx * Math.cos(a), 0, cz + rz * Math.sin(a)));
      }
    };

    // Build path: 4 arcs connected by straight edges (implicit via arc endpoints)
    // Top-right corner
    addArc(ihx - rx, ihz - rz, 0);
    // Top-left corner
    addArc(-(ihx - rx), ihz - rz, Math.PI / 2);
    // Bottom-left corner
    addArc(-(ihx - rx), -(ihz - rz), Math.PI);
    // Bottom-right corner
    addArc(ihx - rx, -(ihz - rz), 3 * Math.PI / 2);

    // Custom linear closed curve — no CatmullRom distortion
    class LinearClosedCurve extends THREE.Curve {
      constructor(points) { super(); this.pts = points; }
      getPoint(t) {
        const n = this.pts.length;
        const f = t * n;
        const i = Math.floor(f) % n;
        const j = (i + 1) % n;
        const frac = f - Math.floor(f);
        return new THREE.Vector3().lerpVectors(this.pts[i], this.pts[j], frac);
      }
    }

    const curve = new LinearClosedCurve(pts);
    const tubeGeo = new THREE.TubeGeometry(curve, 256, tubeRadius, 8, true);

    pt.geometry.dispose();
    pb.geometry.dispose();
    pt.geometry = tubeGeo;
    pb.geometry = tubeGeo.clone();

    // Y at the outer corner edge — where the flat top meets the bevel curve
    const pipingY = t / 2 - inset;
    pt.position.y = pipingY;
    pb.position.y = -pipingY;
  }, [l, t, w, pipingOption, pipingColor, pipingImageUrl, fabricColor, fabricImageUrl, fabricZoom]);

  const handleReset = () => {
    resetCameraRef.current = true;
  };

  return (
    <div
      className="w-full h-[280px] sm:h-[480px] rounded-xl overflow-hidden relative"
      style={{ background: 'linear-gradient(135deg, #f5f0eb 0%, #e8e0d8 100%)' }}
      data-testid="premium-3d-preview"
    >
      {fabricLoading && <FabricLoadingOverlay />}
      {!fabricLoading && pipingLoading && <FabricLoadingOverlay label="Loading piping..." />}
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} data-testid="three-canvas-container" />

      {/* Dimension labels — projected from 3D to 2D */}
      <div
        ref={el => dimLabelsRef.current.length = el}
        className="absolute pointer-events-none bg-white/85 backdrop-blur-sm px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-semibold text-gray-700 -translate-x-1/2 whitespace-nowrap shadow-sm"
        style={{ opacity: 0, transition: 'opacity 0.15s' }}
        data-testid="dim-label-length"
      >
        {rawLength > 0 ? `${unit === 'mm' ? rawLength * 10 : rawLength} ${unit}` : `L ${unit}`}
      </div>
      <div
        ref={el => dimLabelsRef.current.width = el}
        className="absolute pointer-events-none bg-white/85 backdrop-blur-sm px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-semibold text-gray-700 -translate-x-1/2 whitespace-nowrap shadow-sm"
        style={{ opacity: 0, transition: 'opacity 0.15s' }}
        data-testid="dim-label-width"
      >
        {rawWidth > 0 ? `${unit === 'mm' ? rawWidth * 10 : rawWidth} ${unit}` : `D ${unit}`}
      </div>
      <div
        ref={el => dimLabelsRef.current.thickness = el}
        className="absolute pointer-events-none bg-white/85 backdrop-blur-sm px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-semibold text-gray-700 -translate-x-1/2 whitespace-nowrap shadow-sm"
        style={{ opacity: 0, transition: 'opacity 0.15s' }}
        data-testid="dim-label-thickness"
      >
        {rawThickness ? `${unit === 'mm' ? rawThickness * 10 : rawThickness} ${unit} thick` : `T ${unit}`}
      </div>

      {/* Zip legend — top left */}
      {showZip && (
      <div className="hidden sm:flex absolute top-3 left-3 items-center gap-2 bg-white/85 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs text-gray-600 pointer-events-none" data-testid="zip-legend">
        <svg width="24" height="6" viewBox="0 0 24 6" className="flex-shrink-0">
          <line x1="0" y1="3" x2="24" y2="3" stroke="#333" strokeWidth="2" strokeDasharray="3,2" />
        </svg>
        <span className="font-medium">Zip placement</span>
      </div>
      )}

      {/* Reset button — top right */}
      <button
        onClick={handleReset}
        className="absolute top-3 right-3 w-8 h-8 bg-white/85 backdrop-blur-sm rounded-full flex items-center justify-center text-gray-500 hover:text-gray-800 hover:bg-white transition-colors shadow-sm cursor-pointer"
        title="Reset view"
        data-testid="reset-view-btn"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 12a9 9 0 0 1-15.36 6.36"/>
          <path d="M3 12a9 9 0 0 1 15.36-6.36"/>
          <polyline points="21 3 21 9 15 9"/>
          <polyline points="3 21 3 15 9 15"/>
        </svg>
      </button>

      <InteractionHints className="hidden sm:flex absolute bottom-3 left-1/2 -translate-x-1/2 items-center gap-3 bg-white/80 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs text-gray-500 pointer-events-none whitespace-nowrap" />
    </div>
  );
};

export default PremiumCushionPreview;
